import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../../../hooks/toast';
import { useAuth } from '../../../hooks/auth';
import { Screens } from '../../../shared/enums/screens.enum';
import { PermissionActions } from '../../../shared/enums/permissions.enum';
import {
  AButton,
  ASelect,
  ATextArea,
  ATable,
  AModal,
  DropdownOptions,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../../components';
import {
  ContactsOutlined,
  PlusCircleOutlined,
  RedoOutlined,
  WhatsAppOutlined,
  PhoneOutlined,
  MailOutlined,
  EditOutlined,
  HistoryOutlined,
  UserOutlined,
  CloseOutlined,
  SearchOutlined,
  CalendarOutlined,
  DeleteOutlined,
  ReloadOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import {
  Card,
  Col,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Input,
  Row,
  Space,
  Tag,
  Badge,
  Popconfirm,
} from 'antd';
import dayjs from 'dayjs';
import { getCrcContacts,
  ICrcContact,
  postCrcContact,
  patchCrcContact,
} from '../../../http/crc';
import { crcDeleteContact } from '../../../http/crc';
import { getSelectCrcAtendentes, getSelectUsers } from '../../../http/users';
import { getSelectClients } from '../../../http/clients';
import { IClient, IUser } from '../../../shared/interfaces';

type CrcCanal = 'WHATSAPP' | 'PHONE' | 'EMAIL';
type CrcMotivo =
  | 'AGENDAMENTO'
  | 'CONFIRMACAO'
  | 'REMARCADO'
  | 'FALTA'
  | 'POS_ATENDIMENTO'
  | 'TRATAMENTO_INTERROMPIDO'
  | 'COBRANCA'
  | 'DUVIDA'
  | 'RECLAMACAO'
  | 'OUTROS';
type CrcSituacao = 'PENDENTE' | 'CONFIRMADO' | 'FINALIZADO' | 'AGUARDANDO' | 'REAGENDADO';

type ContactSituation = CrcSituacao;

const SITUATION_OPTIONS: {
  value: ContactSituation;
  label: string;
  color: string;
}[] = [
  { value: 'PENDENTE', label: 'Pendente', color: 'orange' },
  { value: 'CONFIRMADO', label: 'Confirmado', color: 'green' },
  { value: 'FINALIZADO', label: 'Finalizado', color: 'default' },
  { value: 'AGUARDANDO', label: 'Aguardando', color: 'gold' },
  { value: 'REAGENDADO', label: 'Reagendado', color: 'purple' },
];

const CHANNEL_OPTIONS: {
  value: CrcCanal;
  label: string;
  icon: React.ReactNode;
  color: string;
}[] = [
  { value: 'WHATSAPP', label: 'WhatsApp', icon: <WhatsAppOutlined />, color: '#25D366' },
  { value: 'PHONE', label: 'Telefone', icon: <PhoneOutlined />, color: '#1677ff' },
  { value: 'EMAIL', label: 'E-mail', icon: <MailOutlined />, color: '#fa8c16' },
];

const REASON_OPTIONS: { value: CrcMotivo; label: string }[] = [
  { value: 'AGENDAMENTO', label: 'Agendamento' },
  { value: 'CONFIRMACAO', label: 'Confirmação de consulta' },
  { value: 'REMARCADO', label: 'Remarcação' },
  { value: 'FALTA', label: 'Falta / Reagendamento' },
  { value: 'POS_ATENDIMENTO', label: 'Pós-atendimento' },
  { value: 'TRATAMENTO_INTERROMPIDO', label: 'Tratamento interrompido' },
  { value: 'COBRANCA', label: 'Cobrança / Pagamento' },
  { value: 'DUVIDA', label: 'Dúvida' },
  { value: 'RECLAMACAO', label: 'Reclamação' },
  { value: 'OUTROS', label: 'Outros' },
];

const RESULT_OPTIONS: { value: string; label: string }[] = [
  { value: 'CONSULTA_AGENDADA', label: 'Consulta agendada' },
  { value: 'CONSULTA_CONFIRMADA', label: 'Consulta confirmada' },
  { value: 'REAGENDADO', label: 'Reagendado' },
  { value: 'SEM_RESPOSTA', label: 'Sem resposta' },
  { value: 'NAO_INTERESSADO', label: 'Não interessado' },
  { value: 'ASSUNTO_RESOLVIDO', label: 'Assunto resolvido' },
  { value: 'PENDENTE_RETORNO', label: 'Pendente de retorno' },
];

const SITUATION_FILTER = SITUATION_OPTIONS;
const NOOP = () => undefined;

const contactFromServer = (rec: any): ICrcContact => {
  const id = rec.id;
  const client_name = rec.client_name ?? rec.name ?? '';
  return {
    id,
    client_id: rec.client_id ?? null,
    client_name,
    name: client_name,
    phone: rec.phone ?? '',
    email: rec.email ?? null,
    status: (rec.status ?? rec.situacao ?? 'PENDENTE') as any,
    origin: (rec.origin ?? rec.canal ?? 'MANUAL') as any,
    last_contact_at: rec.updated_at ?? rec.created_at ?? new Date().toISOString(),
    next_follow_up_at: null,
    notes: rec.observacoes ?? rec.notes ?? null,
    owner_user_id: rec.atendente_user_id ?? rec.owner_user_id ?? null,
    owner_name: (rec as any).owner_name ?? null,
    created_at: rec.created_at ?? new Date().toISOString(),
    updated_at: rec.updated_at ?? new Date().toISOString(),
    canal: rec.canal ?? null,
    motivo: rec.motivo ?? null,
    resultado: rec.resultado ?? null,
    situacao: rec.situacao ?? null,
    protocolo: rec.protocolo ?? null,
  } as ICrcContact & {
    canal?: string | null;
    motivo?: string | null;
    resultado?: string | null;
    situacao?: string | null;
    protocolo?: string | null;
  };
};

const CrcContacts: React.FC = () => {
  const { addToast } = useToast();
  const { screenFind, permissionFind, user } = useAuth();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [situationFilter, setSituationFilter] = useState<CrcSituacao | undefined>();
  const [canalFilter, setCanalFilter] = useState<CrcCanal | undefined>();
  const [userIdFilter, setUserIdFilter] = useState<string | undefined>();

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [pages, setPages] = useState<number>(0);

  const [contacts, setContacts] = useState<ICrcContact[]>([]);
  const [users, setUsers] = useState<IUser[]>([]);
  const [clients, setClients] = useState<IClient[]>([]);

  const [selectedContact, setSelectedContact] = useState<any | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [editingContact, setEditingContact] = useState<any | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [historyOpen, setHistoryOpen] = useState<boolean>(false);
  const [historyClient, setHistoryClient] = useState<any | null>(null);
  const [historyItems, setHistoryItems] = useState<any[]>([]);

  const userIdOwner = user?.id;

  const loadUsers = useCallback(async () => {
    const safe = (x: any): IUser[] => {
      if (!x) return [];
      if (Array.isArray(x)) return x;
      if (Array.isArray(x.data)) return x.data;
      if (Array.isArray(x.list)) return x.list;
      return [];
    };
    try {
      const list = await getSelectCrcAtendentes();
      const cleaned = safe(list);
      console.log('[CRC Contacts] loadUsers -> getSelectCrcAtendentes:', list, '-> cleaned:', cleaned);
      setUsers(cleaned);
    } catch (e) {
      console.warn('[CRC Contacts] loadUsers crc error:', e);
      try {
        const fb = safe(await getSelectUsers());
        console.log('[CRC Contacts] loadUsers fallback select:', fb);
        setUsers(fb);
      } catch {
        setUsers([]);
      }
    }
  }, []);

  const loadClients = useCallback(async () => {
    try {
      const data = await getSelectClients(1000);
      setClients(data ?? []);
    } catch {
      setClients([]);
    }
  }, []);

  const ownerNameById = useMemo(() => {
    const map: Record<string, string> = {};
    const arr = Array.isArray(users) ? users : [];
    arr.forEach((u) => (map[u.id] = u.name));
    return map;
  }, [users]);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        limit,
        offset: (page - 1) * limit,
        search: search || undefined,
        atendente_user_id: userIdFilter,
        situacao: situationFilter,
        canal: canalFilter,
      };
      const res = await getCrcContacts(params);
      const mapped = (res.data ?? []).map(contactFromServer);
      setContacts(mapped);
      setPages(res.count ?? 0);
    } catch (err: any) {
      setContacts([]);
      setPages(0);
      const msg = err?.response?.data?.message ?? err?.message ?? 'Verifique sua conexão.';
      addToast({
        type: 'error',
        title: 'Erro ao carregar contatos',
        description: msg,
      });
    } finally {
      setLoading(false);
    }
  }, [limit, page, search, userIdFilter, situationFilter, canalFilter, addToast]);

  useEffect(() => {
    loadUsers();
    loadClients();
  }, [loadUsers, loadClients]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const renderChannelTag = (canalRaw?: string | null) => {
    const key = (canalRaw || '').toUpperCase();
    const ch = CHANNEL_OPTIONS.find((c) => c.value === key) ?? CHANNEL_OPTIONS[CHANNEL_OPTIONS.length - 1];
    return (
      <Tag color={ch.color} icon={ch.icon}>
        {ch.label}
      </Tag>
    );
  };

  const renderSituationTag = (situationRaw?: string | null) => {
    const key = (situationRaw || '').toUpperCase();
    const s = SITUATION_OPTIONS.find((o) => o.value === key);
    if (s) return <Tag color={s.color}>{s.label}</Tag>;
    return <Tag color="default">{key || '-'}</Tag>;
  };

  const renderReasonLabel = (motivoRaw?: string | null) => {
    const key = (motivoRaw || '').toUpperCase();
    const r = REASON_OPTIONS.find((o) => o.value === key);
    return r?.label ?? key ?? '-';
  };

  const renderResultLabel = (resultadoRaw?: string | null) => {
    const key = (resultadoRaw || '').toUpperCase();
    const r = RESULT_OPTIONS.find((o) => o.value === key);
    if (r) return r.label;
    if (!key) return '-';
    const map: Record<string, string> = {
      SCHEDULED: 'Consulta agendada',
      CONFIRMED: 'Consulta confirmada',
      RESCHEDULED: 'Reagendado',
      NO_ANSWER: 'Sem resposta',
      NOT_INTERESTED: 'Não interessado',
      FINISHED: 'Assunto resolvido',
      PENDING: 'Pendente de retorno',
    };
    return map[key] ?? key;
  };

  const handleNewContact = () => {
    setEditingContact(null);
    form.resetFields();
    form.setFieldsValue({
      canal: 'WHATSAPP',
      motivo: 'OUTROS',
      resultado: 'PENDENTE_RETORNO',
      situacao: 'PENDENTE',
      atendente_user_id: userIdOwner ?? undefined,
    });
    setModalOpen(true);
  };

  const handleEditContact = (contact: any) => {
    setEditingContact(contact);
    const motivo = (contact.motivo || 'OUTROS').toUpperCase();
    const canal = (contact.canal || 'WHATSAPP').toUpperCase();
    const situacao = (contact.situacao || contact.status || 'PENDENTE').toUpperCase();
    const resultado = contact.resultado || 'PENDENTE_RETORNO';
    form.setFieldsValue({
      client_id: contact.client_id ?? undefined,
      client_name: contact.client_name ?? contact.name ?? '',
      phone: contact.phone ?? '',
      canal: CHANNEL_OPTIONS.some((c) => c.value === canal) ? canal : 'WHATSAPP',
      motivo: REASON_OPTIONS.some((r) => r.value === motivo) ? motivo : 'OUTROS',
      resultado,
      situacao: SITUATION_OPTIONS.some((s) => s.value === situacao) ? situacao : 'PENDENTE',
      observacoes: contact.notes ?? contact.observacoes ?? '',
      atendente_user_id: contact.owner_user_id ?? contact.atendente_user_id ?? userIdOwner ?? undefined,
    });
    setModalOpen(true);
  };

  const handleDelete = async (contact: any) => {
    try {
      setLoading(true);
      await (crcDeleteContact as any)(contact.id);
      addToast({
        type: 'success',
        title: 'Contato removido',
        description: 'Contato excluído com sucesso.',
      });
      await loadContacts();
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Erro ao remover',
        description: err?.response?.data?.message ?? err?.message ?? 'Verifique sua conexão.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitModal = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      try {
        const selectedClient = clients.find((c) => c.id === values.client_id);
        const clientNameRaw = (values.client_name || selectedClient?.corporate_name || '').trim();
        const payload: any = {
          client_id: values.client_id ?? null,
          client_name: clientNameRaw || 'Contato Manual',
          phone: values.phone || null,
          canal: values.canal ?? 'WHATSAPP',
          motivo: values.motivo ?? 'OUTROS',
          situacao: values.situacao ?? 'PENDENTE',
          resultado: values.resultado ?? null,
          observacoes: values.observacoes ?? null,
          atendente_user_id: values.atendente_user_id ?? userIdOwner ?? null,
        };

        if (editingContact) {
          await patchCrcContact(editingContact.id, payload as any);
          addToast({
            type: 'success',
            title: 'Contato atualizado',
            description: 'Alterações salvas com sucesso.',
          });
        } else {
          await postCrcContact(payload as any);
          addToast({
            type: 'success',
            title: 'Contato criado',
            description: 'Contato registrado no banco de dados.',
          });
        }
        setModalOpen(false);
        setEditingContact(null);
        form.resetFields();
        setPage(1);
        await loadContacts();
      } catch (err: any) {
        const msg =
          Array.isArray(err?.response?.data?.message)
            ? err.response.data.message.join(', ')
            : err?.response?.data?.message ?? err?.message ?? 'Tente novamente.';
        addToast({
          type: 'error',
          title: editingContact ? 'Erro ao atualizar contato' : 'Erro ao criar contato',
          description: msg,
        });
      }
    } catch {
      addToast({
        type: 'warning',
        title: 'Atenção',
        description: 'Verifique os campos obrigatórios.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const openHistory = (contact: any) => {
    setHistoryClient(contact);
    const items: any[] = [];
    items.push({
      id: `h-c-${contact.id}`,
      date: dayjs(contact.created_at).format('DD/MM/YYYY HH:mm'),
      channel: renderChannelTag(contact.canal) as any,
      reason: renderReasonLabel(contact.motivo),
      result: renderResultLabel(contact.resultado),
      attendant: ownerNameById[contact.owner_user_id ?? contact.atendente_user_id] ?? '-',
    });
    setHistoryItems(items);
    setHistoryOpen(true);
  };

  return (
    <PageLayout>
      <PageIntro
        badge="CRC"
        icon={<ContactsOutlined />}
        title="Central de Contatos"
        description="Gerencie todos os contatos com seus pacientes: confirmações, lembretes, retornos, reagendamentos e muito mais."
      />
      <MenuOptions
        showInputSearch
        search={search}
        setSearch={setSearch}
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => loadContacts(),
          },
          ...(permissionFind([PermissionActions.crcContactsCreate])
            ? [
                {
                  label: 'Novo Contato',
                  key: '2',
                  icon: <PlusCircleOutlined />,
                  onClick: () => handleNewContact(),
                },
              ]
            : []),
        ]}
      />
      <PagePanel>
        <Card bordered={false} size="small" style={{ marginBottom: 16 }} title="Filtros">
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} md={8}>
              <Input
                placeholder="Buscar por paciente, telefone, protocolo ou observações..."
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} md={4}>
              <ASelect
                style={{ width: '100%' }}
                allowClear
                placeholder="Situação"
                value={situationFilter}
                onChange={(v) => setSituationFilter(v as any)}
                options={SITUATION_FILTER.map((s) => ({ label: s.label, value: s.value }))}
              />
            </Col>
            <Col xs={24} md={4}>
              <ASelect
                style={{ width: '100%' }}
                allowClear
                placeholder="Canal"
                value={canalFilter}
                onChange={(v) => setCanalFilter(v as any)}
                options={CHANNEL_OPTIONS.map((c) => ({ label: c.label, value: c.value }))}
              />
            </Col>
            <Col xs={24} md={4}>
              <ASelect
                style={{ width: '100%' }}
                allowClear
                placeholder="Atendente"
                value={userIdFilter}
                onChange={(v) => setUserIdFilter(v)}
                options={(Array.isArray(users) ? users : []).map((u) => ({ label: u.name, value: u.id }))}
              />
            </Col>
            <Col xs={24} md={4}>
              <AButton
                type="primary"
                block
                icon={<ReloadOutlined />}
                onClick={() => {
                  setPage(1);
                  loadContacts();
                }}
              >
                Buscar
              </AButton>
            </Col>
          </Row>
        </Card>
      </PagePanel>
      <PagePanel>
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={selectedContact ? 16 : 24}>
            <Card
              bordered={false}
              size="small"
              title={
                <Space>
                  <ContactsOutlined />
                  Contatos ({contacts.length}/{pages})
                </Space>
              }
              extra={
                permissionFind([PermissionActions.crcContactsCreate]) ? (
                  <AButton
                    type="primary"
                    icon={<PlusCircleOutlined />}
                    onClick={() => handleNewContact()}
                  >
                    + Novo Contato
                  </AButton>
                ) : null
              }
            >
              <ATable
                loading={loading}
                scroll={{ x: 1200, y: 520 }}
                dataSource={contacts}
                rowKey="id"
                columns={[
                  {
                    title: 'Data / Hora',
                    dataIndex: 'last_contact_at',
                    width: 160,
                    render: (v: string | undefined | null, rec: any) =>
                      dayjs(v ?? rec.created_at).format('DD/MM/YYYY HH:mm'),
                  },
                  {
                    title: 'Protocolo',
                    dataIndex: 'protocolo',
                    width: 170,
                    render: (v: string | undefined | null, rec: any) =>
                      v ? (
                        <Tag color="blue" icon={<FileTextOutlined />}>
                          {v}
                        </Tag>
                      ) : (
                        <span style={{ color: '#999', fontSize: 11 }}>
                          #{(rec.id ?? '').slice(0, 8)}
                        </span>
                      ),
                  },
                  {
                    title: 'Paciente',
                    dataIndex: 'client_name',
                    width: 220,
                    render: (v: string | undefined | null, rec: any) => (
                      <Space>
                        <UserOutlined />
                        <span>
                          <div style={{ fontWeight: 600 }}>{v ?? rec.name ?? '-'}</div>
                          <div style={{ fontSize: 11, color: '#999' }}>{rec.phone ?? '-'}</div>
                        </span>
                      </Space>
                    ),
                  },
                  {
                    title: 'Canal',
                    dataIndex: 'canal',
                    width: 130,
                    render: (v: any, _r: any) => renderChannelTag(v),
                  },
                  {
                    title: 'Motivo',
                    dataIndex: 'motivo',
                    width: 190,
                    render: (v: any) => renderReasonLabel(v),
                  },
                  {
                    title: 'Resultado',
                    dataIndex: 'resultado',
                    width: 190,
                    render: (v: any) => renderResultLabel(v),
                  },
                  {
                    title: 'Situação',
                    dataIndex: 'situacao',
                    width: 130,
                    render: (v: any) => renderSituationTag(v || (v as any)?.status),
                  },
                  {
                    title: 'Atendente',
                    width: 150,
                    render: (_v, rec: any) => {
                      const owner =
                        ownerNameById[rec.owner_user_id ?? rec.atendente_user_id] ??
                        (rec as any).owner_name;
                      if (owner) return <Badge status="processing" text={owner} />;
                      return <span style={{ color: '#999' }}>-</span>;
                    },
                  },
                  {
                    title: 'Ações',
                    width: 230,
                    fixed: 'right',
                    render: (cell: any) => (
                      <Space size={4}>
                        <AButton
                          size="small"
                          type="text"
                          icon={<HistoryOutlined />}
                          title="Ver Histórico"
                          onClick={() => openHistory(cell)}
                        >
                          Histórico
                        </AButton>
                        <DropdownOptions
                          size="small"
                          items={[
                            {
                              label: 'Ver detalhes',
                              key: '1',
                              onClick: () => setSelectedContact(cell),
                            },
                            ...(permissionFind([PermissionActions.crcContactsUpdate])
                              ? [
                                  {
                                    label: 'Editar',
                                    key: '2',
                                    icon: <EditOutlined />,
                                    onClick: () => handleEditContact(cell),
                                  },
                                  {
                                    label: 'Excluir',
                                    key: '3',
                                    icon: <DeleteOutlined />,
                                    extra: (
                                      <Popconfirm
                                        key={`del-${cell.id}`}
                                        title="Deseja realmente excluir este contato?"
                                        onConfirm={() => handleDelete(cell)}
                                        okText="Sim"
                                        cancelText="Não"
                                      >
                                        <span />
                                      </Popconfirm>
                                    ),
                                  },
                                ]
                              : []),
                          ]}
                        />
                      </Space>
                    ),
                  },
                ]}
                page={page}
                setPage={setPage}
                pages={pages}
                limit={limit}
                setLimit={setLimit}
                onRow={(rec: any) => ({
                  onClick: () => setSelectedContact(rec),
                  style: { cursor: 'pointer' },
                })}
              />
            </Card>
          </Col>
          {selectedContact && (
            <Col xs={24} xl={8}>
              <Card
                bordered={false}
                size="small"
                style={{ position: 'sticky', top: 8 }}
                title={
                  <Space>
                    <ContactsOutlined />
                    Detalhes do Contato
                  </Space>
                }
                extra={
                  <AButton
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => setSelectedContact(null)}
                  />
                }
              >
                <Descriptions
                  column={1}
                  size="small"
                  bordered
                  labelStyle={{ width: 120, fontWeight: 600 }}
                >
                  <Descriptions.Item label="Protocolo">
                    {selectedContact.protocolo ? (
                      <Tag color="blue" icon={<FileTextOutlined />}>
                        {selectedContact.protocolo}
                      </Tag>
                    ) : (
                      <span style={{ color: '#999' }}>
                        #{(selectedContact.id ?? '').slice(0, 8)}
                      </span>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Paciente">
                    <div style={{ fontWeight: 600 }}>
                      {selectedContact.client_name ?? selectedContact.name ?? '-'}
                    </div>
                  </Descriptions.Item>
                  <Descriptions.Item label="Telefone">
                    {selectedContact.phone ?? '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Canal">
                    {renderChannelTag(selectedContact.canal)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Motivo">
                    {renderReasonLabel(selectedContact.motivo)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Data Criação">
                    {dayjs(selectedContact.created_at).format('DD/MM/YYYY HH:mm')}
                  </Descriptions.Item>
                  <Descriptions.Item label="Resultado">
                    {renderResultLabel(selectedContact.resultado)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Situação">
                    {renderSituationTag(selectedContact.situacao ?? selectedContact.status)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Atendente">
                    {(() => {
                      const owner =
                        ownerNameById[
                          selectedContact.owner_user_id ?? selectedContact.atendente_user_id
                        ] ?? selectedContact.owner_name;
                      if (owner) return <Badge status="processing" text={owner} />;
                      return '-';
                    })()}
                  </Descriptions.Item>
                </Descriptions>
                <Divider style={{ margin: '12px 0' }} />
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                  Observações
                </div>
                <div
                  style={{
                    padding: 10,
                    background: '#fafafa',
                    borderRadius: 6,
                    fontSize: 12,
                    color: '#555',
                    minHeight: 60,
                  }}
                >
                  {selectedContact.notes ??
                    selectedContact.observacoes ??
                    'Nenhuma observação registrada para este contato.'}
                </div>
                <Divider style={{ margin: '12px 0' }} />
                <Space direction="vertical" style={{ width: '100%' }} size={6}>
                  <AButton block icon={<HistoryOutlined />} onClick={() => openHistory(selectedContact)}>
                    Ver Histórico do Paciente
                  </AButton>
                  {permissionFind([PermissionActions.crcContactsUpdate]) && (
                    <AButton
                      block
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={() => handleEditContact(selectedContact)}
                    >
                      Editar Contato
                    </AButton>
                  )}
                </Space>
              </Card>
            </Col>
          )}
        </Row>
      </PagePanel>

      <AModal
        open={modalOpen}
        close={() => setModalOpen(false)}
        title={
          <Space>
            {editingContact ? <EditOutlined /> : <PlusCircleOutlined />}
            {editingContact ? 'Editar Contato' : 'Novo Contato CRC'}
          </Space>
        }
        confirmLoading={submitting}
        onOk={() => handleSubmitModal()}
        width={720}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Canal de Atendimento"
                name="canal"
                rules={[{ required: true, message: 'Selecione o canal' }]}
                initialValue="WHATSAPP"
              >
                <ASelect
                  options={CHANNEL_OPTIONS.map((c) => ({
                    label: (
                      <Space>
                        {c.icon} {c.label}
                      </Space>
                    ),
                    value: c.value,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Motivo do Contato"
                name="motivo"
                rules={[{ required: true, message: 'Selecione o motivo' }]}
                initialValue="OUTROS"
              >
                <ASelect
                  options={REASON_OPTIONS.map((r) => ({
                    label: r.label,
                    value: r.value,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Resultado"
                name="resultado"
                initialValue="PENDENTE_RETORNO"
              >
                <ASelect
                  allowClear
                  options={RESULT_OPTIONS.map((r) => ({
                    label: r.label,
                    value: r.value,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Situação"
                name="situacao"
                rules={[{ required: true, message: 'Selecione a situação' }]}
                initialValue="PENDENTE"
              >
                <ASelect
                  options={SITUATION_OPTIONS.map((s) => ({
                    label: s.label,
                    value: s.value,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Paciente / Cliente" name="client_id">
                <ASelect
                  showSearch
                  allowClear
                  placeholder="Busque e selecione um paciente (opcional)..."
                  filterOption={(input: string, option: any) =>
                    String(option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={clients.map((c) => ({
                    label: `${c.corporate_name}${c.code ? ` (Cód. ${c.code})` : ''}`,
                    value: c.id,
                  }))}
                  onChange={(clientId: string | undefined) => {
                    if (!clientId) return;
                    const selected = clients.find((c) => c.id === clientId);
                    if (!selected) return;
                    const current = form.getFieldsValue();
                    const patchForm: Record<string, any> = { client_id: clientId };
                    if (!current.client_name && selected.corporate_name) {
                      patchForm.client_name = selected.corporate_name;
                    }
                    if (!current.phone && (selected as any).cell_phone) {
                      patchForm.phone = (selected as any).cell_phone;
                    }
                    form.setFieldsValue(patchForm);
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Nome (se não for paciente cadastrado)" name="client_name">
                <Input placeholder="Digite o nome para contato manual" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Telefone / WhatsApp" name="phone">
                <Input placeholder="(11) 90000-0000" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Atendente Responsável" name="atendente_user_id">
                <ASelect
                  allowClear
                  placeholder="Selecione o atendente"
                  options={(Array.isArray(users) ? users : []).map((u) => ({
                    label: u.name,
                    value: u.id,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="Observações" name="observacoes">
                <ATextArea
                  rows={4}
                  placeholder="Adicione observações sobre este contato..."
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </AModal>

      <Drawer
        title={
          <Space>
            <HistoryOutlined />
            Histórico: {historyClient?.client_name ?? historyClient?.name}
          </Space>
        }
        width={780}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        extra={
          <AButton
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={() => setHistoryOpen(false)}
          />
        }
      >
        <ATable
          loading={false}
          dataSource={historyItems}
          rowKey="id"
          columns={[
            { title: 'Data / Hora', dataIndex: 'date', width: 170 },
            { title: 'Canal', dataIndex: 'channel', width: 140 },
            { title: 'Motivo', dataIndex: 'reason', width: 220 },
            { title: 'Resultado', dataIndex: 'result' },
            { title: 'Atendente', dataIndex: 'attendant', width: 140 },
          ]}
          page={1}
          setPage={NOOP}
          pages={historyItems.length}
          limit={10}
          setLimit={NOOP}
        />
      </Drawer>
    </PageLayout>
  );
};

export default CrcContacts;
